import {useAuth} from "../../../../context";
import {useEffect, useState} from "react";
import {centsToString, unitsToCents} from "../utilities/formatCurrency";
import {fetchWrapper} from "../../../../_services/fetch";
import {config} from "../../../../utils2";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";

const api = config.get_remote_api() + "/api/products"

const endpointOrderOptions = () => `${api}/order`
const endpointDetails = (productId) => `${api}/${productId}`
const endpointCount = (userId, search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, search) => `${api}/list${fetchWrapper.getSearchParams(search)}`
const endpointFilteredList = () => `${api}/list`

const endpointAdd = () => `${api}/add`
const endpointEdit = (userId, targetId) => `${api}/${targetId}`
const endpointDelete = (userId, targetId) => `${api}/${targetId}`

const centsToPrices = prices => prices.map(o => ({...o, price: centsToString(o.price)}))
const pricesToCents = prices => prices.map(o => ({...o, price: unitsToCents(o.price)}))

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null


const defaultValues = {
    id: null,
    title: "",
    SKU: "",
    summary: "",
    description: "",
    stock: 0,

    status: "",
    is_available: false,
    tax_percentage: 0,

    note: "",
    alert: "",
    features: "",

    created_at: null,
    updated_at: null,

    prices: [{price: 0, quantity: 1, discount: 0}],
    medias: [],
    related: [],
    tags: [],
    tips: [],
    // user_id: "",
    // items: []
}

export function useProduct(userId, pid = null) {
    const statusMap = ["draft", "active", "archived"]

    const validationSchema = yup.object().shape({
        title: yup.string().required("Title is required"),
        SKU: yup.string().required("SKU is required"),
        summary: yup.string().required("Summary is required").max(160, "Summary must be at most 160 characters"),
        description: yup.string().required("description is required"),
        stock: yup.number().required("Stock is required").min(0, "Stock must be positive"),

        status: yup.string()
            .required("Status is mandatory")
            .oneOf(statusMap, `Status must be one of: ${statusMap.join(", ")}`),
        is_available: yup.bool().required(),
        tax_percentage: yup.number().required().min(0, "Tax must be positive"),
        is_digital: yup.bool().required('Please specify if it is digital'),
        digital_duration: yup.number().when(["is_digital", "digital_type"], {
            is: (is_digital, digital_type) => is_digital === true && digital_type === 1,
            then: (schema) => schema.required('Digital duration is required with digital product')
                .min(1, 'Duration must be at least 1'),
            otherwise: (schema) => schema.notRequired(),
        }),
        subscription_mb: yup.number().when(["is_digital", "digital_type"], {
            is: (is_digital, digital_type) => is_digital === true && digital_type === 1,
            then: (schema) => schema.required('Dimension is required with digital product')
                .min(1, 'Duration must be at least 1'),
            otherwise: (schema) => schema.notRequired(),
        }),

        note: yup.string(),
        alert: yup.string(),
        features: yup.string(),

        prices: yup.array().of(yup.object().shape({
            price: yup.number().required("Missing price").positive("Price must be positive"),
            quantity: yup.number().required("Missing quantity").positive("Quantity must be greater than 0"),
            discount: yup.number().required("Missing discount percentage").min(0, "Discount must be positive"),
        })).min(1, "Minimum 1 price"),
        related: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
        })),
        medias: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
        })).min(1, "Minimum 1 picture"),
        tags: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
            name: yup.string().required(),
            variant: yup.string().required(),
        })),
    })

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useProductDetails(pid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            // setValue(resp?.data ? ({...resp.data, prices: centsToPrices(resp.data.prices)}) : defaultValues);
            setValue(resp?.data ? ({...resp.data}) : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }


    function addItem(values) {
        const productInCents = {...values, prices: pricesToCents(values.prices)}
        const {id, ...valuesNoId} = productInCents
        return fetchWrapper.post(endpointAdd(userId), valuesNoId, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(values, targetId = pid) {
        const productInCents = {...values, prices: pricesToCents(values.prices)}
        return fetchWrapper.put(endpointEdit(userId, targetId), productInCents, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = pid) {
        return fetchWrapper.delete(endpointDelete(userId, targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }


    return {
        value,
        validationSchema,
        statusMap,
        addItem,
        editItem,
        addEditItem: values => pid === "add" ? addItem(values) : editItem(values),
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}


export function useProductDetails(productId, defaultValue = defaultValues) {

    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (productId && productId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(productId), requestOptions)
                .then(handleSuccess)
                // .then(data => setValue({...data, prices: centsToPrices(data.prices)}))
                .then(data => setValue(data))
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [productId]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useProductCount(queryOption = null) {

    const {loggedIn, userId, token} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpointCount(userId, search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useProductList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 25
    const defaultPageIndex = 0

    const {loggedIn, userId, token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            // const iterPrices = data => data.map(item => ({...item, prices: centsToPrices(item.prices)}))
            fetchWrapper.generic(endpointList(userId, search), requestOptions)
                .then(handleSuccess)
                // .then(data => setList(prev => keepPrev ? ([...prev, ...iterPrices(data)]) : iterPrices(data)))
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useProductFilteredList(orderByDef = null, defaultPageSize = 10) {
    const defaultPageIndex = 0

    const [loadingList, setLoadingList] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const [errorList, setErrorList] = useState(null)
    const [list, setList] = useState([])
    const keepPrev = false

    // Search options
    const pageSize = Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = Number(searchParams.get("index")) || defaultPageIndex
    const query = searchParams.get("query")
    const orderBy = searchParams.get("by") || orderByDef

    const tagIds = searchParams.get("tags")
    const priceFrom = searchParams.get("price_from")
    const priceTo = searchParams.get("price_to")
    const inStock = searchParams.get("in_stock")

    useEffect(() => {
        const controller = new AbortController()

        setErrorList(null)
        setLoadingList(true)

        const payload = {
            tag_ids: tagIds == null ? [] : tagIds.includes("_") ? tagIds.split("_") : [tagIds],
            ...(priceFrom != null && {price_from: unitsToCents(priceFrom)}),
            ...(priceTo != null && {price_to: unitsToCents(priceTo)}),
            ...(inStock != null && {in_stock: inStock}),
            offset: pageIndex * pageSize,
            limit: pageSize,
            query: query,
            order_by: orderBy
        }

        // Fetch options
        const requestOptions = {
            method: "POST",
            signal: controller.signal,
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload),
        }


        // const iterPrices = data => data.map(item => ({...item, prices: centsToPrices(item.prices)}))
        fetchWrapper.generic(endpointFilteredList(), requestOptions)
            .then(handleSuccess)
            .then(data => {
                //console.log(data)
                setList(prev => keepPrev ? [...prev, ...data] : data)
            })
            // .then(data => {
            //     setList(prev => keepPrev ? ([...prev, ...iterPrices(data)]) : iterPrices(data))
            //     // console.log(iterPrices(data))
            // })
            .catch(err => setErrorList(handleError(err)))
            .finally(() => setLoadingList(false))


        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [pageSize, pageIndex, orderBy, query, tagIds, priceFrom, priceTo, inStock]);

    return {
        list,
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}


export function useProductOrderOptions(defaultOptions = []) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}